/**
 * The customers' side of the Support Centre.
 *
 *   "where do the submissions go from when someone on the website is on the
 *    Get in Touch page and submits their information? it should be in the
 *    Support Centre - the customers side of things."
 *
 * They now go to contact_submissions and arrive here. Before script 61 they
 * went nowhere at all: the marketing form set a flag and discarded the message.
 *
 * ONE THING TO KNOW ABOUT THIS QUEUE: it is the only screen in the portal that
 * shows a name and an email address without a captured reason first. That is
 * correct rather than an oversight — the person WROTE IN, unprompted, asking to
 * be contacted. Their name and address are the message. Gating that behind a
 * reason capture would be ceremony, and canon reserves reason capture for
 * opening a record somebody did not offer.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

export async function fetchCustomerEnquiries() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_customer_enquiry_queue');
  if (error) return fail(error.message || 'The customer queue could not be read.');
  return { data: data || [], error: null };
}

/** 'answered', 'closed' or 'spam'. The last two need a reason. */
export async function resolveEnquiry(id, status, note) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_resolve_customer_enquiry', {
    p_id: id, p_status: status, p_note: note || null,
  });
  if (error) return fail(error.message || 'That enquiry could not be updated.');
  return { data, error: null };
}

/**
 * Which of the shared Queue's four states an enquiry is in.
 *
 * 'spam' sits under Rejected rather than getting a fifth tab: it is a decision
 * that the enquiry will not be answered, which is what Rejected means, and it
 * carries its reason like every other rejection.
 */
export const enquiryState = (row) => {
  if (row.status === 'answered') return 'settled';
  if (row.status === 'closed' || row.status === 'spam') return 'rejected';
  return 'waiting';
};

/**
 * Tickets queued for one portal card and section.
 *
 * Script 138. The Support Centre's own screen reads contact_submissions -- the
 * marketing contact form -- and nothing in this portal has ever read
 * support_tickets, which is why an Enterprise enquiry raised from a provider's
 * billing screen landed correctly and was invisible.
 *
 * Both arguments are optional. Passing neither is "everything on my desks",
 * which is what the Support Centre wants; passing both is one section, which is
 * what a finance officer opening Tier changes wants.
 */
export async function fetchTicketsOnMyDesk(department = null, section = null) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_tickets_on_my_desk', {
    p_department: department, p_section: section,
  });
  if (error) return fail(error.message || 'That queue could not be read.');
  return { data: data || [], error: null };
}

/**
 * Which of the shared Queue's four states a ticket is in.
 *
 * IT USED TO RETURN A FIFTH, AND NOTHING HAD EVER CALLED IT TO FIND OUT.
 *
 * `assigned` and `in_progress` returned `'working'`. `QUEUE_STATES` is
 * `['waiting', 'settled', 'rejected', 'all']` — there is no `working` tab, and
 * the counts object seeds only those four, so `acc['working']` is undefined and
 * the increment is skipped. A ticket somebody was actively working on was
 * therefore **invisible on every tab except All, and counted on none of them**.
 *
 * It survived because it was exported and unused: script 138 added it alongside
 * `fetchTicketsOnMyDesk`, and the one card that reads tickets (Tier Changes)
 * draws its own list rather than using the shared Queue. The desk built on 14
 * September is the first caller, and the first render showed nothing.
 *
 * A ticket being worked on is not settled and not rejected, so `waiting` is the
 * Queue's word for it — "still open, still ours". The distinction is not lost:
 * the desk prints the ticket's own status on the row, which is a truer place
 * for it than a tab, because `in_progress` and `assigned` are different things
 * and one tab could not have said which.
 */
export const ticketState = (row) => {
  if (row.status === 'resolved' || row.status === 'closed') return 'settled';
  return 'waiting';
};

/**
 * ---------------------------------------------------------------------------
 * THE DESK ITSELF, WHICH THE PORTAL HAS NEVER HAD.
 *
 * Measured on 14 September: fourteen `%ticket%` functions in the database and
 * exactly ONE called by any JavaScript in any of the three repositories --
 * `fn_tickets_on_my_desk`, above, by the Tier Changes card, for the single
 * section `finance` / `modify_subscription_tier`. The queue, the event trail,
 * assignment and every state transition were unreachable, so a ticket raised
 * anywhere else arrived correctly and was never seen.
 *
 * EVERY ONE OF THESE GATES ON A PERMISSION, NOT A ROLE. Each function opens
 * with `admin_has_permission(auth.uid(), 'respond_to_ticket')` -- Prohibition 5
 * -- so a desk without that permission is refused by the database, not by this
 * file. Nothing here re-checks it: a client-side gate on a permission is a
 * second answer to a question only one party is entitled to answer.
 */

/**
 * The whole queue for a stream, or for everything.
 *
 * `p_stream` is 'customer' or 'internal' and is passed even when null, because
 * the function's signature carries no default.
 */
export async function fetchTicketQueue(stream = null) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_ticket_queue', { p_stream: stream });
  if (error) return fail(error.message || 'The ticket queue could not be read.');
  return { data: data || [], error: null };
}

/**
 * One ticket's event trail, oldest first.
 *
 * This is the audit record: every transition with who made it, under which OID,
 * and when. `fn_ticket_events_are_append_only` guarantees nothing in it can be
 * rewritten, which is what makes "how long did this take and who answered it" a
 * question with an answer.
 */
export async function fetchTicketDetail(id) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_ticket_detail', { p_id: id });
  if (error) return fail(error.message || 'That ticket could not be opened.');
  return { data: data || [], error: null };
}

/**
 * Put it on a desk: a person or an agent, never both.
 *
 * `support_ticket_one_desk` is a CHECK constraint and `fn_assign_ticket` refuses
 * it in words -- "A ticket goes to one desk: a person or an agent." Both
 * arguments are sent every time so the caller cannot half-specify.
 */
export async function assignTicket(id, { admin = null, agent = null } = {}) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_assign_ticket', {
    p_id: id, p_admin: admin, p_agent: agent,
  });
  if (error) return fail(error.message || 'That ticket could not be assigned.');
  return { data, error: null };
}

/**
 * Move it, and say why.
 *
 * The database owns which moves are legal -- `fn_ticket_move_is_allowed` lists
 * thirteen pairs and refuses everything else by name: "A ticket cannot go from
 * resolved to triaged." MOVES is that same list, and it is here only so the
 * screen can offer the moves that will work rather than every status and a
 * refusal. It is checked against the database by a test rather than trusted.
 */
export async function moveTicket(id, to, note = null) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_move_ticket', {
    p_id: id, p_to: to, p_note: note,
  });
  if (error) return fail(error.message || 'That ticket could not be moved.');
  return { data, error: null };
}

/** The thirteen legal transitions, mirroring fn_ticket_move_is_allowed. */
export const MOVES = {
  raised:      ['triaged', 'closed'],
  triaged:     ['assigned', 'closed'],
  assigned:    ['in_progress', 'triaged'],
  in_progress: ['resolved', 'assigned'],
  resolved:    ['closed', 'reopened'],
  closed:      ['reopened'],
  reopened:    ['assigned', 'in_progress'],
};

export const movesFrom = (status) => MOVES[status] || [];

/** What each move is called on a button. The verb is what the desk is about to
 *  do, not the status name it will land on. */
export const MOVE_WORDS = {
  triaged:     'Triage it',
  assigned:    'Put it on a desk',
  in_progress: 'Start work',
  resolved:    'Resolve it',
  closed:      'Close it',
  reopened:    'Reopen it',
};
